/**
 * HLF Search Popup — Custom Element
 * Full-screen search modal with predictive search, trending terms, and recommendations.
 */
class HlfSearchPopup extends HTMLElement {
  constructor() {
    super();
    this._input = null;
    this._defaultContent = null;
    this._resultsContent = null;
    this._resultsInner = null;
    this._clearBtn = null;
    this._cache = {};
    this._abortController = null;
    this._debounceTimer = null;
    this._moneyFormat = '';
    this._bound = {};
  }

  connectedCallback() {
    this._input = this.querySelector('[data-sp-input]');
    this._defaultContent = this.querySelector('[data-sp-default]');
    this._resultsContent = this.querySelector('[data-sp-results]');
    this._resultsInner = this.querySelector('[data-sp-results-inner]');
    this._clearBtn = this.querySelector('[data-sp-clear]');
    this._moneyFormat = this.dataset.moneyFormat || '₹{{amount}}';

    // Close buttons (backdrop + close btn)
    this.querySelectorAll('[data-sp-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Clear button
    if (this._clearBtn) {
      this._clearBtn.addEventListener('click', () => {
        this._input.value = '';
        this._input.focus();
        this._updateClear(false);
        this._showDefault();
      });
    }

    // Input handler
    if (this._input) {
      this._input.addEventListener('input', this._onInput.bind(this));
      // Submit form on Enter when no results are focused
      this._input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const q = this._input.value.trim();
          if (q) {
            window.location.href = `/search?q=${encodeURIComponent(q)}&options[prefix]=last`;
            e.preventDefault();
          }
        }
      });
    }

    // Escape key
    this._bound.onKeydown = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', this._bound.onKeydown);
  }

  disconnectedCallback() {
    if (this._bound.onKeydown) {
      document.removeEventListener('keydown', this._bound.onKeydown);
    }
  }

  open() {
    this.removeAttribute('hidden');
    this.removeAttribute('closing');
    // Force reflow so transition triggers
    this.offsetHeight; // eslint-disable-line no-unused-expressions
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => {
      if (this._input) this._input.focus();
    }, 80);
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.setAttribute('closing', '');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    // Abort any running fetch
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }

    const cleanup = () => {
      this.removeAttribute('closing');
      this.setAttribute('hidden', '');
      if (this._input) this._input.value = '';
      this._updateClear(false);
      this._showDefault();
    };

    // Wait for transition to finish
    const panel = this.querySelector('.hlf-sp__panel');
    if (panel) {
      const onEnd = () => {
        panel.removeEventListener('transitionend', onEnd);
        cleanup();
      };
      panel.addEventListener('transitionend', onEnd);
      // Fallback timeout
      setTimeout(() => {
        if (this.hasAttribute('closing')) {
          panel.removeEventListener('transitionend', onEnd);
          cleanup();
        }
      }, 400);
    } else {
      cleanup();
    }
  }

  /* ---- Input Handler ---- */
  _onInput() {
    clearTimeout(this._debounceTimer);
    const q = this._input.value.trim();

    if (!q) {
      this._showDefault();
      this._updateClear(false);
      return;
    }

    this._updateClear(true);
    this._debounceTimer = setTimeout(() => this._fetchResults(q), 280);
  }

  /* ---- Predictive Search Fetch ---- */
  async _fetchResults(q) {
    const key = q.toLowerCase();

    if (this._cache[key]) {
      this._renderResults(this._cache[key], q);
      return;
    }

    // Abort any previous request
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    this._showResults();
    this._resultsInner.innerHTML = '<div class="hlf-sp__loading"><div class="hlf-sp__spinner"></div></div>';

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
      const resp = await fetch(url, { signal: this._abortController.signal });

      if (!resp.ok) throw new Error(resp.status);

      const data = await resp.json();
      this._cache[key] = data;
      this._renderResults(data, q);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._resultsInner.innerHTML = '<p class="hlf-sp__no-results">Something went wrong. Please try again.</p>';
    }
  }

  /* ---- Render Results ---- */
  _renderResults(data, query) {
    const results = data.resources?.results;
    const products = results?.products || [];
    const queries = results?.queries || [];

    if (products.length === 0 && queries.length === 0) {
      this._resultsInner.innerHTML = `
        <div class="hlf-sp__no-results">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <p>No results found for "<strong>${this._esc(query)}</strong>"</p>
          <p class="hlf-sp__no-results-hint">Try different keywords or check spelling</p>
        </div>`;
      this._showResults();
      return;
    }

    let html = '';

    // Query suggestions
    if (queries.length > 0) {
      html += '<div class="hlf-sp__result-section"><h4 class="hlf-sp__result-heading">Suggestions</h4>';
      html += '<ul class="hlf-sp__result-list">';
      queries.forEach(q => {
        html += `<li><a href="${q.url}" class="hlf-sp__result-query">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <span>${q.styled_text}</span>
        </a></li>`;
      });
      html += '</ul></div>';
    }

    // Products
    if (products.length > 0) {
      html += '<div class="hlf-sp__result-section"><h4 class="hlf-sp__result-heading">Products</h4>';
      html += '<div class="hlf-sp__result-products">';
      products.forEach(p => {
        const img = p.image
          ? `<img src="${p.image}" alt="${this._esc(p.title)}" loading="lazy" width="80" height="80">`
          : '<div style="width:100%;height:100%;background:#eee;"></div>';
        const price = this._formatMoney(p.price);
        const comparePrice = p.compare_at_price ? this._formatMoney(p.compare_at_price) : null;
        let priceHtml = price;
        let badgeHtml = '';

        if (comparePrice && parseFloat(p.compare_at_price) > parseFloat(p.price)) {
          priceHtml = `${price} <s class="hlf-sp__rec-compare-price">${comparePrice}</s>`;
          const savedAmount = parseFloat(p.compare_at_price) - parseFloat(p.price);
          const savedStr = this._formatMoney(savedAmount.toString());
          badgeHtml = `<span class="hlf-sp__badge">Save ${savedStr}</span>`;
        }

        html += `<a href="${p.url}" class="hlf-sp__result-product">
          <div class="hlf-sp__result-product-img">${img}${badgeHtml}</div>
          <div class="hlf-sp__result-product-info">
            <p class="hlf-sp__result-product-title">${this._esc(p.title)}</p>
            <p class="hlf-sp__result-product-price">${priceHtml}</p>
          </div>
        </a>`;
      });
      html += '</div></div>';
    }

    // View all link
    html += `<a href="/search?q=${encodeURIComponent(query)}&options[prefix]=last" class="hlf-sp__view-all">
      View all results
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
    </a>`;

    this._resultsInner.innerHTML = html;
    this._showResults();
  }

  /* ---- Helpers ---- */
  _formatMoney(priceStr) {
    // priceStr from the suggest API is a decimal string like "2999.00"
    // Shopify money_format uses {{ amount }}, {{ amount_with_comma_separator }}, etc.
    const amount = parseFloat(priceStr || '0');
    const formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    const withCommas = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this._moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(amount).toString())
      .replace(/\{\{\s*amount\s*\}\}/, formatted);
  }

  _esc(str) {
    const el = document.createElement('span');
    el.textContent = str || '';
    return el.innerHTML;
  }

  _showDefault() {
    if (this._defaultContent) this._defaultContent.removeAttribute('hidden');
    if (this._resultsContent) this._resultsContent.setAttribute('hidden', '');
  }

  _showResults() {
    if (this._defaultContent) this._defaultContent.setAttribute('hidden', '');
    if (this._resultsContent) this._resultsContent.removeAttribute('hidden');
  }

  _updateClear(show) {
    if (this._clearBtn) {
      this._clearBtn.style.display = show ? 'flex' : 'none';
    }
  }
}

customElements.define('hlf-search-popup', HlfSearchPopup);
